import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import roleService from '../../services/roleService';
import { useStore } from '../../context/StoreContext';
import AppDataGrid from '../common/AppDataGrid';
import GridActionsCell from '../common/GridActionsCell';
import TableSearch from '../common/TableSearch';
import ActiveFilter from '../common/ActiveFilter';
import { useDataTable } from '../../hooks/useDataTable';
import { buildToggleAction, useCanUpdate } from '../common/activeToggle';

const RolesList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSuperAdmin } = useStore();
  const canToggle = useCanUpdate('roles.update');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  // Page, size, search, sort and the active filter live in the URL (/roles?page=0&size=20&...).
  const table = useDataTable(roleService.getRolesPage, {}, {
    onError: () => toast.error(t('roles.fetchError')),
  });
  const fetchRoles = table.refresh;

  const handleToggleActive = async (row) => {
    try {
      await roleService.toggleActive(row.id);
      toast.success(row.isActive === false ? t('common.activated') : t('common.deactivated'));
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.toggleError'));
    }
  };

  const handleDelete = async () => {
    try {
      await roleService.deleteRole(roleToDelete.id);
      toast.success(t('roles.deleteSuccess'));
      fetchRoles();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.deleteError'));
      console.error(error);
    }
  };

  const columns = [
    { field: 'id', headerName: t('common.id'), width: 70 },
    { field: 'name', headerName: t('common.name'), width: 200 },
    { field: 'displayName', headerName: t('common.displayName'), width: 200 },
    ...(isSuperAdmin ? [{
      field: 'storeName',
      headerName: t('roles.store'),
      width: 200,
      cellRenderer: (params) => (
        params.value ? (
          <Chip label={params.value} color="info" size="small" />
        ) : (
          <Chip label={t('common.globalTemplate')} color="warning" size="small" />
        )
      )
    }] : []),
    {
      field: 'description',
      headerName: t('common.description'),
      flex: 1,
      minWidth: 200
    },
    {
      field: 'permissions',
      headerName: t('roles.permissions'),
      width: 130,
      sortable: false,
      cellRenderer: (params) => (
        <Chip label={params.value?.length || 0} color="primary" size="small" />
      )
    },
    {
      field: 'userCount',
      headerName: t('users.title'),
      width: 100,
      cellRenderer: (params) => (
        <Chip label={params.value || 0} color="secondary" size="small" />
      )
    },
    {
      field: 'isActive',
      headerName: t('common.status'),
      width: 110,
      cellRenderer: (params) => (
        <Chip
          label={params.value ? t('common.active') : t('common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 150,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'view',
              icon: ViewIcon,
              title: t('common.details'),
              color: 'info',
              onClick: () => navigate(`/roles/view/${params.data.id}`),
            },
            {
              key: 'edit',
              icon: EditIcon,
              title: t('common.edit'),
              color: 'primary',
              onClick: () => navigate(`/roles/edit/${params.data.id}`),
            },
            // Template (global) roles are blueprints — not toggled from the list.
            buildToggleAction({
              row: params.data,
              onToggle: handleToggleActive,
              t,
              canToggle: canToggle && !params.data.isGlobalRole,
            }),
            {
              key: 'delete',
              icon: DeleteIcon,
              title: t('common.delete'),
              color: 'error',
              disabled: params.data.isGlobalRole,
              onClick: () => {
                setRoleToDelete(params.data);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('roles.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/roles/create')}
        >
          {t('roles.createRole')}
        </Button>
      </Box>

      <Card sx={{ mb: 2, p: 2 }}>
        <TableSearch
          placeholder={t('common.search')}
          value={table.searchInput}
          onChange={table.handleSearchChange}
        />
      </Card>

      <ActiveFilter
        value={table.params.active || 'all'}
        onChange={(active) => table.updateParams({ active })}
        sx={{ mb: 2 }}
      />

      <Card>
        <AppDataGrid
          rows={table.data.content}
          columns={columns}
          loading={table.loading}
          paginationMode="server"
          rowCount={table.data.totalElements}
          paginationModel={table.paginationModel}
          onPaginationModelChange={table.handlePaginationModelChange}
          sortModel={table.sortModel}
          onSortModelChange={table.handleSortChange}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowId={(row) => row.id}
        />
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('roles.deleteRole')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('roles.deleteConfirm', { name: roleToDelete?.displayName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesList;
